// BRIEF 211 PART EIGHT [RULED 24] — A MEMBER'S OWN REFERENCES IN A COLLECTIVE CASE.
//
// « A contract number belongs to the client's place in a collective case:
// several per place, each case its own. […] A case's quantum is its own when
// set, the client's otherwise (RULINGS 2026-08-10, brief 61 §0): whatever a
// case's table, add window or import file states is that case's, never the
// client's. »
//
// Both live on the member's `clientLinks` element (docs/211-deploy-note.md
// §8.7): `contractNumbers`, a list in the order typed — the roster junction
// does not carry it, as it does not carry `importMarks` — and `quantum`, the
// member's own, which the junction mirrors (61 §0; absent reads the client's).
// `Client.contractNumber` is the client's own « Réf. contrat » and none of this
// file's business.
//
// One file, the browser's and the server's (a `.mjs` loads unchanged in both —
// collectiveSegments.mjs's reason): the members table, the add window, the
// import and export, the sort and search, the dues join; the document merge,
// the clients list's search and §20's one-shot on the server. Pure.

/**
 * A member's contract numbers in this case — the element's list, each a trimmed string, empties dropped.
 * @param {{ contractNumbers?: unknown } | null | undefined} lk
 * @returns {string[]}
 */
export function memberNumbers(lk) {
  const raw = lk && Array.isArray(lk.contractNumbers) ? lk.contractNumbers : [];
  return raw.map((x) => (x === null || x === undefined ? "" : String(x).trim())).filter((x) => x !== "");
}

/**
 * Numbers as a reader sees them — the table, the cards, a document: « , »-separated.
 * @param {readonly string[]} list
 */
export const numbersShown = (list) => list.join(", ");

/**
 * Numbers as the export writes them, and the import reads them back: « ; »-separated.
 * @param {readonly string[]} list
 */
export const numbersWritten = (list) => list.join("; ");

/**
 * A quantum as the export writes it: the number the member reads, or an empty cell when it reads none — a 0 in
 * its place would come back through the import as this case's own quantum of 0 (§18: export, then import,
 * unchanged). The compute reads none and 0 alike (`+(quantum || 0)`), so no due moves.
 * @param {unknown} q
 * @returns {number | ""}
 */
export function quantumWritten(q) {
  if (q === null || q === undefined || q === "") return "";
  const n = Number(q);
  return Number.isFinite(n) ? n : "";
}

/**
 * Text into numbers. A file's cell splits on « ; » alone (§18: a contract number may hold a comma); what a
 * person types in the table or the add window splits on « ; » or « , » (what the table shows is « , »).
 * @param {unknown} text
 * @param {{ comma?: boolean }} [opts]
 * @returns {string[]}
 */
export function parseNumbers(text, opts = {}) {
  const s = text === null || text === undefined ? "" : String(text);
  return s
    .split(opts.comma === false ? /;/ : /[;,]/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
}

/**
 * The list with `add` beside it, in order — never a number removed, each once (§18: an import never removes a number).
 * @param {readonly string[]} list
 * @param {readonly string[]} add
 * @returns {string[]}
 */
export function withNumbers(list, add) {
  const out = [...list];
  for (const n of add) if (!out.includes(n)) out.push(n);
  return out;
}

/**
 * A link carrying exactly `list` as its numbers — the key removed when there are none.
 * @template {object} T
 * @param {T} lk
 * @param {readonly string[]} list
 * @returns {T & { contractNumbers?: string[] }}
 */
export function withMemberNumbers(lk, list) {
  /** @type {T & { contractNumbers?: string[] }} */
  const out = Object.assign({}, lk);
  if (list.length) out.contractNumbers = [...list];
  else delete out.contractNumbers;
  return out;
}

/**
 * A member's own quantum in this case, or null when it has none (the element's `quantum`).
 * @param {{ quantum?: unknown } | null | undefined} lk
 * @returns {number | null}
 */
export function ownQuantum(lk) {
  const v = lk ? lk.quantum : null;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * A member's quantum in this case: its own when set, else the client's AS STORED (61 §0:
 * `lk.quantum ?? client.quantum`) — so a member with none reads, and posts to the compute, exactly what it did.
 * @param {{ quantum?: unknown } | null | undefined} lk
 * @param {{ quantum?: unknown } | null | undefined} client
 * @returns {unknown}
 */
export function memberQuantum(lk, client) {
  const own = ownQuantum(lk);
  if (own !== null) return own;
  return client ? client.quantum ?? null : null;
}

/**
 * A link with its own quantum set to `q`, or cleared — null: the member reads the client's again (§17).
 * @template {object} T
 * @param {T} lk
 * @param {number | null | undefined} q
 * @returns {T & { quantum?: number }}
 */
export function withOwnQuantum(lk, q) {
  /** @type {T & { quantum?: number }} */
  const out = Object.assign({}, lk);
  if (q === null || q === undefined || !Number.isFinite(q)) delete out.quantum;
  else out.quantum = q;
  return out;
}

/** @type {WeakMap<object, Map<number, string[]>>} */
const BY_CASES = new WeakMap();

/**
 * 211 §19 — every number a client holds in any collective case, by client id: what the clients list finds a
 * client by, beside its own « Réf. contrat ». Read once per collective-case list (kept against the list itself,
 * so an edited list is read afresh).
 * @param {unknown} collectiveCases
 * @returns {Map<number, string[]>}
 */
export function numbersByClient(collectiveCases) {
  if (!Array.isArray(collectiveCases)) return new Map();
  const hit = BY_CASES.get(collectiveCases);
  if (hit) return hit;
  /** @type {Map<number, string[]>} */
  const out = new Map();
  for (const cc of collectiveCases) {
    const links = cc && Array.isArray(cc.clientLinks) ? cc.clientLinks : [];
    for (const lk of links) {
      const id = Number(lk && typeof lk === "object" ? lk.clId : NaN);
      const ns = memberNumbers(lk && typeof lk === "object" ? lk : null);
      if (!Number.isFinite(id) || !ns.length) continue;
      out.set(id, withNumbers(out.get(id) ?? [], ns));
    }
  }
  BY_CASES.set(collectiveCases, out);
  return out;
}
